import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  closeModal,
  initialQueryState,
  openModal,
  showToastMessage,
  updateQueryStateTable,
  useTableStore,
} from 'enable-ui';

import { useAuthStore, userProfileSelector } from 'modules/auth/core';
import { MODAL_KEY } from 'modules/auth/core/modal-key';

import type { Courses, Lessons } from '../type';

import { trimValue } from 'helpers';
import { setUrlParams } from 'helpers/url';
import { useNumber, useString } from 'hooks';
import { apiCall } from 'hooks/service';
import { useUpdateQueryState } from 'hooks/url';
import { cloneDeep, debounce, isUndefined } from 'lodash';

import { SUCCESS_MESSAGE } from 'constants/message';
import { PATH } from 'constants/path';
import { TABLE_KEY } from 'constants/table';

export const useLessonsController = () => {
  const navigationTo = useNavigate();

  const params = useParams();

  const { institutionId } = params;

  useUpdateQueryState(TABLE_KEY.LESSONS, 12);

  const queryState = useTableStore((s) => s.state.queryState[TABLE_KEY.LESSONS]);

  const userProfile = useAuthStore(userProfileSelector);

  const [lessonsData, setLessonsData] = useState<Lessons[]>([]);

  const [selectedLessons, setSelectedLessons] = useState<Record<string, string>>({});

  const searchValue = useString('');

  const searchLesson = useString('');

  const lessonSelectedId = useString('');

  const [coursesData, setCoursesData] = useState<Courses[]>([]);

  const total = useNumber(0);

  useEffect(() => {
    if (!userProfile?.id) {
      return;
    }

    if (queryState) {
      setUrlParams(queryState);

      searchLesson.setValue(queryState.search ?? '');

      getListLessons();
    }
  }, [JSON.stringify(queryState), userProfile]);

  useEffect(() => {
    getListCourses();

    updateQueryStateTable(TABLE_KEY.LESSONS, {
      ...initialQueryState,
      limit: 12,
    });

    return () => {
      updateQueryStateTable(TABLE_KEY.LESSONS, {
        ...initialQueryState,
        limit: 12,
      });
    };
  }, []);

  const getListCourses = async (search: string = '') => {
    const result: {
      items: Courses[];
      total: number;
    } = await apiCall({
      url: '/courses',
      method: 'GET',
      isLoading: true,
      data: {
        page: 1,
        limit: 10,
        institutionId: userProfile?.institution?.id ?? institutionId,
        search: search,
      },
    });

    if (result?.items) {
      setCoursesData(result?.items ?? []);
    }
  };

  const getListLessons = async () => {
    const result: {
      items: Lessons[];
      total: number;
    } = await apiCall({
      url: '/lessons',
      method: 'GET',
      isLoading: true,
      showError: true,
      data: {
        ...queryState,
        institutionId: userProfile?.institution?.id ?? institutionId,
      },
    });

    if (result?.items) {
      setLessonsData(result?.items ?? []);
    }

    if (result?.total) {
      total.setValue(result.total);
    }
  };

  const onChangeSelectedLessons = (lesson: string) => () => {
    const newSelected = cloneDeep(selectedLessons);

    if (!isUndefined(newSelected[lesson])) {
      delete newSelected[lesson];
    } else {
      newSelected[lesson] = lesson;
    }

    setSelectedLessons(newSelected);
  };

  const onChangeSearchLesson = (event: React.ChangeEvent<HTMLInputElement>) => {
    searchLesson.setValue(event.target.value);
    debounceSearch(event.target.value);
  };

  const debounceSearch = useCallback(
    debounce((newSearch: string) => {
      updateQueryStateTable(TABLE_KEY.LESSONS, {
        ...queryState,
        page: 1,
        search: trimValue(newSearch),
      });
    }, 500),
    [],
  );

  const onChangeSearchValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    searchValue.setValue(event.target.value);
    debounceSearchValue(event.target.value);
  };

  const debounceSearchValue = useCallback(
    debounce((newSearch: string) => {
      getListCourses(trimValue(newSearch));
    }, 500),
    [],
  );

  const handleAddToCourse = async (courseId: string) => {
    for (const el of Object.keys(selectedLessons)) {
      await apiCall({
        url: '/courses-lessons',
        method: 'POST',
        data: {
          lessonId: selectedLessons[el],
          courseId: courseId,
        },
        isLoading: true,
        showError: true,
      });
    }

    showToastMessage('success', SUCCESS_MESSAGE.ADD_LESSON_TO_COURSE);
  };

  const handleEditLesson = (lessonId: string) => () => {
    navigationTo(PATH.EDIT_LESSONS.replace(':id', lessonId));
  };

  const handleDeleteLesson = (lessonId: string) => async () => {
    openModal(MODAL_KEY.DELETE_LESSON);
    lessonSelectedId.setValue(lessonId);
  };

  const goToLessonDetail = (lessonId: string) => {
    navigationTo(PATH.EDIT_LESSONS.replace(':id', lessonId));
  };

  const callbackFirstPage = () => {
    updateQueryStateTable(TABLE_KEY.LESSONS, {
      ...initialQueryState,
      page: 1,
      limit: 12,
    });
  };

  const onDeleteLesson = async () => {
    const responsive: {
      statusCode: number;
    } = await apiCall({
      url: `/lessons/${lessonSelectedId.value}`,
      method: 'DELETE',
      isLoading: true,
      showError: true,
    });

    closeModal(MODAL_KEY.DELETE_LESSON);

    if (responsive.statusCode === 400) {
      return;
    }

    getListLessons();
    setSelectedLessons({});
    callbackFirstPage();
    showToastMessage('success', SUCCESS_MESSAGE.DELETE_LESSON);
  };

  const handleDelete = async () => {
    const result: string[] = [];

    for (const el of Object.keys(selectedLessons)) {
      const response: { id: string } = await apiCall({
        url: `/lessons/${selectedLessons[el]}`,
        method: 'DELETE',
        isLoading: true,
        showError: true,
      });

      if (response?.id) {
        result.push(response.id);
      }
    }

    if (result.length === Object.keys(selectedLessons).length) {
      callbackFirstPage();
      showToastMessage('success', SUCCESS_MESSAGE.DELETE_LESSONS);
    }

    setSelectedLessons({});
    getListLessons();
  };

  const handleDuplicateLesson = (lessonId: string) => async () => {
    const result: Lessons = await apiCall({
      url: `/lessons/${lessonId}/duplicate`,
      method: 'POST',
      isLoading: true,
      showError: true,
    });

    if (!result?.id) {
      return;
    }

    showToastMessage('success', 'Lesson duplicated successfully!');

    getListLessons();
  };

  return {
    lessonsData,
    total,
    onChangeSelectedLessons,
    selectedLessons,
    setSelectedLessons,
    queryState,
    searchLesson,
    onChangeSearchLesson,
    searchValue,
    onChangeSearchValue,
    handleAddToCourse,
    handleEditLesson,
    handleDeleteLesson,
    goToLessonDetail,
    onDeleteLesson,
    handleDelete,
    coursesData,
    handleDuplicateLesson,
  };
};
